const SELECTORS = {
  form: "#custom-product-form",
  container: "#form-ai-generate #custom-product-form",
  resultDiv: "#form-ai-generate #ai-image-result",
  aiQuantityInput:
    '#ai-result-actions quantity-selector-component input[type="number"]',
  aiAddToCartBtn: "#ai-add-to-cart-btn",
  formAiGenerate: "#form-ai-generate",
  generateBtn: "#generate-btn",
  generateAiImageInput: "#generate-ai-image",
  customUpload: "custom-upload-input",
  optionalFields: "#custom-description-field",
  imageSelectionField: "#custom-image-selection-field",
  imageRadioInputs: ".image-radio-input",
  aiFields: ".ai-field",
  requiredFields: ".ai-field.required",
  dropdownGrid: ".dropdown-grid",
  dynamicOptions: '[id^="dynamic_option_id_"]',
  dpoRequired: '[validation*="required"]',
  dpoContainer: ".custom-popup-dpo-container",
  productQuantityInput:
    '.product-page-form-buttons quantity-selector-component input[type="number"]',
  designSelectionInput: 'input[name="properties[Selected Design]"]',
};

const MESSAGES = {
  generating: "Generating...",
  generateButton: "Generate Artwork Mockup",
  generatingPreview: "<p>Generating your AI preview...</p>",
  noImage: "<p>No image received yet. Please try again.</p>",
  error:
    '<p style="color:red;">Error generating preview. Please try again.</p>',
  required: "This field is required",
};

const API_URL =
  "https://circlelogic.app.n8n.cloud/webhook/38f31e17-7eb4-4023-b038-69a2ec67c9fd";

const Validator = {
  isValid(el) {
    const { type, checked, files, value } = el;

    if (el.tagName === "CUSTOM-UPLOAD-INPUT") {
      return el.validate() && el.isValidType();
    }

    if (type === "checkbox" || type === "radio") return checked;
    if (type === "file") return files?.length > 0;
    if (type === "email") return value && el.checkValidity();
    return value?.trim() !== "";
  },

  showError(el, show) {
    const wrapper = el.closest(".field") || el.parentElement;
    if (!wrapper) return;

    el.classList.toggle("ai-field--error", show);
    wrapper.querySelector(".validation-advice")?.remove();
    if (show) {
      const advice = document.createElement("div");
      advice.className = "validation-advice";
      advice.textContent = MESSAGES.required;
      wrapper.appendChild(advice);
    }
  },

  validateAll(container, showErrors = false) {
    if (!container) return false;

    let isValid = true,
      firstInvalid = null;

    container.querySelectorAll(SELECTORS.requiredFields).forEach((el) => {
      const valid = this.isValid(el);
      if (!valid) {
        isValid = false;
        firstInvalid ||= el;
      }
      if (showErrors) this.showError(el, !valid);
    });

    if (firstInvalid && showErrors) {
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalid.focus?.();
    }

    return isValid;
  },

  isDPOValid(form) {
    return (
      form &&
      Array.from(form.querySelectorAll(SELECTORS.dpoRequired)).every((block) =>
        block.querySelector(
          'input:checked:not([value=""]), select option:checked:not([value=""])',
        ),
      )
    );
  },

  setupLiveValidation(container) {
    container?.querySelectorAll(SELECTORS.requiredFields).forEach((el) => {
      const clearError = () => this.isValid(el) && this.showError(el, false);
      el.addEventListener("input", clearError);
      el.addEventListener("change", clearError);
    });
  },
};

const Collector = {
  async collectUpload(formData, component, selectedImageUrl, imageCache) {
    const file = component?.getFile?.();
    const input = component?.getInput?.();
    if (file && input?.name) {
      formData.append(input.name, file);
    } else if (selectedImageUrl && input?.name) {
      try {
        let blob = imageCache?.get(selectedImageUrl);

        if (!blob) {
          console.log("Cache miss, fetching:", selectedImageUrl);
          const response = await fetch(selectedImageUrl, {
            method: "GET",
            mode: "cors",
            credentials: "omit",
          });
          blob = await response.blob();
          imageCache?.set(selectedImageUrl, blob);
        } else {
          console.log("Cache hit:", selectedImageUrl, blob.size, "bytes");
        }

        const urlParts = selectedImageUrl.split("/");
        const filename =
          urlParts[urlParts.length - 1].split("?")[0] || "selected-image.jpg";

        formData.append(input.name, blob, filename);
      } catch (error) {
        console.error("Failed to convert image URL to file:", error);
        formData.append(input.name, selectedImageUrl);
      }
    }
  },

  collectFields(container, formData) {
    container?.querySelectorAll(SELECTORS.aiFields).forEach((el) => {
      if (!el.name || el.disabled) return;
      if ((el.type !== "checkbox" && el.type !== "radio") || el.checked) {
        formData.append(el.name, el.value);
      }
    });
  },

  collectDropdowns(formData) {
    document.querySelectorAll(SELECTORS.dropdownGrid).forEach((el) => {
      if (el.dataset?.name)
        formData.append(el.dataset.name, el.dataset.value || "");
    });
  },

  collectDPO() {
    const result = {};
    document.querySelectorAll(SELECTORS.dynamicOptions).forEach((block) => {
      const title = block.querySelector("label.required")?.textContent?.trim();
      if (!title) return;

      const input = block.querySelector("input:checked, select");
      if (!input) return;

      const isSelect = input.tagName === "SELECT";
      const value = isSelect
        ? input.options[input.selectedIndex]?.text?.trim() || ""
        : block
            .querySelector(`label[for="${input.id}"] span`)
            ?.textContent?.trim() || input.value;

      result[title] = value;
    });
    return result;
  },

async collectAll(container, uploadComponent, selectedImageUrl, imageCache) {
  const formData = new FormData();

  await this.collectUpload(
    formData,
    uploadComponent,
    selectedImageUrl,
    imageCache,
  );

  this.collectFields(container, formData);
  this.collectDropdowns(formData);

  /* =========================
     Design Brief (Optional)
     ========================= */
  const designBrief = document.querySelector(
    '#custom-description-field textarea, #custom-description-field input'
  );
  if (designBrief?.value?.trim()) {
    formData.append("design_brief", designBrief.value.trim());
  }

  /* =========================
     DPO Options (Readable)
     ========================= */
  const dpo = this.collectDPO();
  formData.append("dpo_options_readable", JSON.stringify(dpo));

  /* =========================
     Optional Add-ons (DPO 1003)
     ========================= */
  const optionalAddOnBlock = document.querySelector('#dynamic_option_id_1003');

  if (optionalAddOnBlock) {
    const checked = optionalAddOnBlock.querySelector(
      'input[type="radio"]:checked'
    );

    if (checked) {
      const labelText = optionalAddOnBlock.querySelector(
        `label[for="${checked.id}"] span`
      )?.textContent?.trim();

      if (labelText && labelText.toLowerCase() !== "none") {
        formData.append("optional_add_ons", labelText);
      }
    }
  }

  formData.append("page_url", window.location.href);

  return formData;
},
};

const API = {
  async generate(formData) {
    const response = await fetch(API_URL, {
      method: "POST",
      body: formData,
    });

    const text = await response.text();

    try {
      return text ? JSON.parse(text) : {};
    } catch (e) {
      return {};
    }
  },
};

class AIForm {
  constructor() {
    this.form = document.querySelector(SELECTORS.form);
    this.container = document.querySelector(SELECTORS.container);
    this.resultDiv = document.querySelector(SELECTORS.resultDiv);
    this.generateBtn = this.form?.querySelector(SELECTORS.generateBtn);
    this.upload = this.form?.querySelector(SELECTORS.customUpload);
    this.optionalField = document.querySelector(SELECTORS.optionalFields);
    this.dpoContainer = document.querySelector(SELECTORS.dpoContainer);
    this.formAiGenerate = document.querySelector(SELECTORS.formAiGenerate);
    this.selectedImageUrl = null;
    this.imageCache = new Map(); // Cache blobs by URL
  }

  init() {
    if (!this.container || !this.form || !this.generateBtn) {
      return;
    }

    this.productQty = document.querySelector(SELECTORS.productQuantityInput);
    Validator.setupLiveValidation(this.container);
    this.generateBtn.addEventListener("click", () => this.generate());
    this.setupListeners();
    this.setupQuantitySync();
    this.setupAddToCart();
    this.updateUI();
  }

  setupListeners() {
    const update = () => this.updateUI();

    this.form.addEventListener(
      "input",
      (e) => e.target?.classList.contains("ai-field") && update(),
    );
    this.form.addEventListener(
      "change",
      (e) => e.target?.matches(".product-custom-option, select") && update(),
    );
    this.upload?.addEventListener("file-selected", update);
    this.upload?.addEventListener("file-removed", update);
    this.upload?.addEventListener("file-error", update);
    window.addEventListener("dpo_loading_finished", update);
    window.jQuery && jQuery(window).on("dpoPriceUpdate", update);
  }

  hasFile() {
    return this.upload?.getFile?.() != null || this.selectedImageUrl != null;
  }

  hasUploadedFile() {
    return this.upload?.getFile?.() != null;
  }

  getSelectedImageUrl() {
    return this.selectedImageUrl;
  }

  updateUI() {
    const allValid =
      Validator.validateAll(this.container) && Validator.isDPOValid(this.form);

    this.upload && (this.upload.style.display = allValid ? "block" : "none");
    this.optionalField &&
      (this.optionalField.style.display = allValid ? "flex" : "none");

    if (allValid) {
      this.generateBtn.style.display = this.hasFile() ? "block" : "none";
      const btnText = this.generateBtn.querySelector(".btn-text");
      if (btnText) btnText.textContent = MESSAGES.generateButton;
    } else {
      this.generateBtn.style.display = "none";
    }
  }

  setGenerating(isLoading) {
    if (!this.generateBtn) return;
    this.generateBtn.disabled = isLoading;
    this.generateBtn.classList.toggle("loading", isLoading);
    const btnText = this.generateBtn.querySelector(".btn-text");
    if (btnText) {
      btnText.textContent = isLoading
        ? MESSAGES.generating
        : MESSAGES.generateButton;
    }
    this.dpoContainer &&
      (this.dpoContainer.style.display = isLoading ? "none" : "");
  }

  showResult(imageUrl) {
    if (!this.resultDiv) return;

    this.resultDiv.innerHTML = imageUrl
      ? `
        <h4>Your AI-Generated Preview:</h4>
        <img src="${imageUrl}" alt="AI preview">
        <div class="design-selection-container" style="margin-top: 2rem;">
          <label class="custom-label required" style="display: block; margin-bottom: 0.5rem; text-transform: uppercase;">Select Your Design:</label>
          <div class="design-options" style="display: flex; gap: 1rem; flex-wrap: wrap;">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="radio" name="properties[Selected Design]" value="Design 1" required checked="true">
              <span>Design 1</span>
            </label>
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="radio" name="properties[Selected Design]" value="Design 2">
              <span>Design 2</span>
            </label>
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="radio" name="properties[Selected Design]" value="Design 3">
              <span>Design 3</span>
            </label>
          </div>
        </div>
        <div class="waiver-notice">
          <strong>NOTE:</strong> A HUMAN BEING WILL CHECK AND FINALISE ARTWORK WITH YOU VIA EMAIL BEFORE STARTING PRODUCTION. SO ANY CHANGES CAN BE MADE AT THIS POINT.
        </div>
        <div class="contact-info">
          <div class="contact-info__name">Andrew Gideon</div>
          <div class="contact-info__title">Director</div>
          <div class="contact-info__list">
            <div class="contact-info__item">
              <a href="tel:+61410007200" class="contact-info__link">+61 410 007 200 (Australia)</a>
            </div>
            <div class="contact-info__item">
              <a href="tel:+971502897511" class="contact-info__link">+971 50 289 7511 (Dubai)</a>
            </div>
            <div class="contact-info__item">
              <a href="tel:+19295436288" class="contact-info__link">+1 929 543 6288 (USA)</a>
            </div>
            <div class="contact-info__item">
              <a href="tel:+447400056722" class="contact-info__link">+44 740 0056 722 (UK)</a>
            </div>
            <div class="contact-info__item">
              <a href="tel:+12892777516" class="contact-info__link">+1 289 277 7516 (Canada)</a>
            </div>
            <div class="contact-info__item">
              <a href="mailto:Andrew@metalxcustom.com" class="contact-info__link">Andrew@metalxcustom.com</a>
            </div>
          </div>
          <div class="contact-info__signature">
            <img src="https://mail.google.com/mail/u/0?ui=2&ik=d071046aba&attid=0.1.1&permmsgid=msg-f:1856257885830898574&th=19c2c0b65877e38e&view=fimg&fur=ip&permmsgid=msg-f:1856257885830898574&sz=s0-l75-ft&attbid=ANGjdJ99EEDlUjUTfvHqWG8Q_eX8mOZ3WrGDLzo62A_nmqsOwBFtGyLmbUInNwRAvaCy_ddt7LZs5MK-CH4Mx6nmeEBUw_JOd0cmnDcFST9JPVv5EGJW755nAcE3n34&disp=emb&zw" alt="Andrew Gideon Signature">
          </div>
        </div>
      `
      : MESSAGES.noImage;

    imageUrl && this.formAiGenerate?.classList.add("has-result");

    document.querySelector(SELECTORS.generateAiImageInput).value =
      imageUrl || "";
  }

  setupQuantitySync() {
    this.aiQty = document.querySelector(SELECTORS.aiQuantityInput);
    if (!this.aiQty || !this.productQty) return;

    const getMin = () => Math.max(1, parseInt(this.productQty.min) || 1);
    let syncing = false;

    const sync = (val) => {
      if (syncing) return;
      syncing = true;

      const min = getMin();
      const value = Math.max(min, parseInt(val) || min);

      this.aiQty.value = this.productQty.value = value;
      this.aiQty.dispatchEvent(new Event("change", { bubbles: true }));
      this.productQty.dispatchEvent(new Event("input", { bubbles: true }));
      this.productQty.dispatchEvent(new Event("change", { bubbles: true }));

      [this.aiQty, this.productQty].forEach((input) => {
        const component = input.closest("quantity-selector-component");
        if (!component) return;

        const val = Math.max(min, parseInt(input.value) || min);
        const minusBtn = component.querySelector(".quantity-minus");
        const plusBtn = component.querySelector(".quantity-plus");

        minusBtn && (minusBtn.disabled = val <= min);
        plusBtn && (plusBtn.disabled = false);
      });

      syncing = false;
    };

    this.aiQty.value = this.productQty.value || "1";

    [this.aiQty, this.productQty].forEach((input) => {
      input.addEventListener("input", () => sync(input.value));
      input.addEventListener("change", () => sync(input.value));
      input
        .closest("quantity-selector-component")
        ?.addEventListener("click", () =>
          setTimeout(() => sync(input.value), 50),
        );
    });

    sync(this.productQty.value);
  }

  setupAddToCart() {
    const btn = document.querySelector(SELECTORS.aiAddToCartBtn);
    btn?.addEventListener("click", (e) => {
      this.updatePriceBreakdown();
      btn.classList.add("atc-added", "submitting");
      window.dpoOptions?.addToCart?.(btn, e);
      setTimeout(() => btn.classList.remove("atc-added", "submitting"), 2000);
    });
  }

  updatePriceBreakdown() {
    const summary = document.querySelector("#summary");
    const priceInput = document.querySelector("#summary-price-breakdown");
    if (!summary || !priceInput) return;

    const breakdown = {};
    const SKIP_LABELS = ["Total", "Summary"];

    summary.querySelectorAll(".custom-price").forEach((span) => {
      let current = span.parentElement;

      while (current && current !== summary) {
        let prev = current.previousElementSibling;
        while (prev) {
          if (prev.tagName === "B") {
            const label = prev.textContent?.replace(":", "").trim();
            const price = span.textContent?.trim();

            if (
              label &&
              price &&
              !SKIP_LABELS.some((skip) => label.includes(skip))
            ) {
              breakdown[label] = price;
            }
            return;
          }
          prev = prev.previousElementSibling;
        }
        current = current.parentElement;
      }
    });

    priceInput.value = JSON.stringify(breakdown);
  }

  async generate() {
    if (!Validator.validateAll(this.container, true)) return;

    this.setGenerating(true);
    this.resultDiv.innerHTML = MESSAGES.generatingPreview;

    try {
      const formData = await Collector.collectAll(
        this.container,
        this.upload,
        this.selectedImageUrl,
        this.imageCache,
      );
      const result = await API.generate(formData);
      this.showResult(result.image_url);
    } catch (err) {
      this.resultDiv.innerHTML = MESSAGES.error;
    } finally {
      this.setGenerating(false);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => new AIForm().init());
